import { Input } from '@apia/theme';
import React from 'react';
import { TApiaFilter, TApiaFilterValue } from '../../../../types';
import ApiaFilter from '../../ApiaFilter';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import useDomProps from '../../../../store/selection/useDomProps';
import { domPropsSelector, rtDebug } from '.';
import useRenderDebug from '../../../../hooks/useRenderDebug';
import { RangeFilter } from './filters/RangeFilter';

interface IFilterCell {
  columnIndex: number;
  filter?: TApiaFilter;
}

const FilterCell = ({ columnIndex, filter }: IFilterCell) => {
  const {
    name,
    onFilterBlur,
    onFilterChange,
    onFilterPressEnter,
    onFilterError,
  } = useResponsiveTableContext();
  const domProps = useDomProps(
    'filterTd',
    {
      columnIndex,
    },
    domPropsSelector(name),
  );

  const handleOnBlur = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (filter && onFilterBlur) onFilterBlur({ ...filter, currentValue });
    },
    [filter, onFilterBlur],
  );

  const handleOnChange = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (filter && onFilterChange) {
        if (filter.type === 'D' || filter.type === 'date') {
          if (onFilterError && filter.error) {
            onFilterError({
              ...filter,
              error: undefined,
            });
          } else if (onFilterError && filter.errorTo) {
            onFilterError({
              ...filter,
              errorTo: undefined,
            });
          } else if (onFilterError && filter.errorFrom) {
            onFilterError({
              ...filter,
              errorFrom: undefined,
            });
          }
        }
        return onFilterChange({
          ...filter,
          error: undefined,
          errorFrom: undefined,
          errorTo: undefined,
          currentValue,
        });
      }
      return true;
    },
    [filter, onFilterChange, onFilterError],
  );

  const handleOnPressEnter = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (filter && onFilterPressEnter)
        onFilterPressEnter({ ...filter, currentValue });
    },
    [filter, onFilterPressEnter],
  );

  useRenderDebug(rtDebug.filterCell, 'FilterCell.tsx', {
    columnIndex,
    filter,
    domProps,
  });
  function findFilter() {
    if (filter && filter.isRange) {
      return <RangeFilter filter={filter} />;
    }
    if (filter && !(filter.asAdditional || !filter.column || filter.isRange)) {
      return (
        <ApiaFilter
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onPressEnter={handleOnPressEnter}
          filter={filter}
        />
      );
    }

    let label: string;
    if (filter && (filter.asAdditional || !filter.column || filter.isRange)) {
      label = window.LBL_ADDITIONAL_FILTER;
    } else {
      label = window.LBL_NO_COLUMN_FILTER;
    }
    return <Input aria-label={label} disabled />;
  }
  return (
    <td
      {...domProps}
      className={`${domProps.className ?? ''} ${
        filter &&
        !filter.asAdditional &&
        (filter.error || (filter.required && filter.currentValue === ''))
          ? 'requiredFilter__Border'
          : ''
      }`}
    >
      {findFilter()}
    </td>
  );
};

export default FilterCell;
